import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Shield,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { toast } from "react-toastify";

const TenantDesignationListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<any>(null);

  const { tenantRoleList, deleteTenantRole } = useAppSelector(
    (state) => state.tenant
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const allRows: any[] = tenantRoleList?.data?.data || [];
  const search = searchInput.trim().toLowerCase();
  const rows = search
    ? allRows.filter((r) => (r?.name || "").toLowerCase().includes(search))
    : allRows;

  useEffect(() => {
    dispatch(tenantActions.getTenantRoleListRequest({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deleteTenantRole?.data?.success) {
      toast.success("Designation deleted successfully");
      dispatch(tenantActions.resetDeleteTenantRole());
      setShowDeleteModal(false);
      setDeleteRow(null);
      dispatch(tenantActions.getTenantRoleListRequest({}));
    } else if (deleteTenantRole?.error) {
      toast.error(`Error: ${deleteTenantRole.error}`);
      dispatch(tenantActions.resetDeleteTenantRole());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTenantRole]);

  const handleClearFilters = () => setSearchInput("");
  const hasActiveFilters = !!searchInput;

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };
  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    dispatch(tenantActions.deleteTenantRoleRequest({ id: deleteRow._id }));
  };

  return (
    <DashboardLayout
      meta={{
        title: "Designations | Invyntra",
        description: "Manage designations and their permissions.",
        keywords: "designations, roles, permissions, team, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Shield size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Designations
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage designations and their permissions for your team.
              </p>
            </div>
          </div>

          {isTenantAdmin && (
            <Button
              rounded="full"
              onClick={() => navigate("/app/team-management/role/create")}
            >
              <Plus size={16} className="mr-1" />
              Add Designation
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search designations..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Designations"
            description="Search and manage your designations."
          />

          <TableV2
            columns={[
              {
                key: "name",
                header: "Designation",
                accessor: ({ key, row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row[key]}
                  </span>
                ),
              },
              {
                key: "description",
                header: "Description",
                accessor: ({ row }) => (
                  <span className="text-gray-600 dark:text-gray-300">
                    {row.description || "-"}
                  </span>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) =>
                  row?.name?.toLowerCase() === "admin" ? (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      No actions
                    </span>
                  ) : !isTenantAdmin ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          <button
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            onClick={() => {
                              close();
                              navigate(
                                `/app/team-management/role/${row._id}/edit`
                              );
                            }}
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                          <button
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                            onClick={() => {
                              close();
                              setDeleteRow(row);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            loading={tenantRoleList.loading}
          />
        </Card>
      </div>

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title="Delete designation"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteTenantRole.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default TenantDesignationListPage;
